import { create } from 'zustand'
import { env } from '../config/env.config'
import { storage } from '../config/storage.config'
import type { PasswordLifecycle } from '../features/supplier/account/types'
import { broadcastSupplierSessionChange } from './auth-session.broadcast'

export interface User {
  id: string
  name: string
  userName: string
  userAccount?: string
  role: string
  dutyName: string
  location: string
  mobilePhone: string | null
  avatar?: string
  accountType?: 'MAIN' | 'CHILD'
  companyCode?: string
  companyName?: string
  permissionVersion?: number
}

export interface UserPermissions {
  roles: string[]
  permissions: string[]
}

export interface ApiMenuItem {
  path: string
  name: string
  component?: string
  meta: {
    title: string
    hidden?: boolean
    alwaysShow?: boolean
    icon?: string
    openMode?: 'route' | 'iframe' | 'external'
    iframeUrl?: string
  }
  children?: ApiMenuItem[] | null
  systemCode?: string
}

function includesMenu(menuList: ApiMenuItem[], name: string): boolean {
  return menuList.some((item) =>
    item.name === name || includesMenu(item.children ?? [], name),
  )
}

function loadMockPreference(key: string): boolean {
  if (!env.dev) return false
  const savedValue = localStorage.getItem(key)
  if (savedValue !== null) return savedValue === 'true'
  return import.meta.env.VITE_USE_MOCK === 'true'
    || localStorage.getItem('dev_menu_source') === 'mock'
}

const maxTimerDelay = 2_147_483_647
let sessionExpiryTimer: ReturnType<typeof setTimeout> | null = null

function clearSessionExpiryTimer() {
  if (sessionExpiryTimer === null) return
  clearTimeout(sessionExpiryTimer)
  sessionExpiryTimer = null
}

function scheduleSessionExpiry(token: string, expiresAtMs: number) {
  clearSessionExpiryTimer()
  const expireWhenDue = () => {
    const remaining = expiresAtMs - Date.now()
    if (remaining <= 0) {
      useAuthStore.getState().expireSessionIfToken(token)
      return
    }
    sessionExpiryTimer = setTimeout(expireWhenDue, Math.min(remaining, maxTimerDelay))
  }
  expireWhenDue()
}

interface AuthStore {
  token: string | null
  user: User | null
  passwordLifecycle: PasswordLifecycle | null
  passwordLifecycleLoaded: boolean
  isLoaded: boolean
  menuMap: Record<string, ApiMenuItem[]>
  permissionsMap: Record<string, UserPermissions>
  supplierAuthenticatedPermissions?: UserPermissions
  mockMenuEnabled: boolean
  mockPermissionsEnabled: boolean
  setLoginData: (token: string, user: User, expiresIn: number) => void
  setSupplierSession: (
    token: string,
    tenantId: string,
    user: User,
    expiresAt: string,
    passwordLifecycle: PasswordLifecycle | null,
    permissions: string[],
  ) => boolean
  setSupplierProfile: (user: User, passwordLifecycle: PasswordLifecycle | null) => void
  setPasswordLifecycle: (passwordLifecycle: PasswordLifecycle | null) => void
  setSupplierPermissions: (permissions: string[]) => void
  setCurrentUser: (user: User) => void
  setSystemData: (systemCode: string, permissions: UserPermissions, menuList: ApiMenuItem[]) => void
  setMockMenuEnabled: (enabled: boolean) => void
  setMockPermissionsEnabled: (enabled: boolean) => void
  setLocation: (location: string) => void
  logout: () => void
  logoutIfToken: (expectedToken: string) => boolean
  expireSessionIfToken: (expectedToken: string) => boolean
  hasPermission: (permission: string, systemCode: string) => boolean
  hasMenu: (name: string, systemCode: string) => boolean
  hasRole: (role: string, systemCode: string) => boolean
}

const initialToken = storage.token.get()
const initialTokenExpiresAt = initialToken ? storage.token.getExpiresAt() : null

export const useAuthStore = create<AuthStore>((set, get) => ({
  token: initialToken,
  user: storage.user.get(),
  passwordLifecycle: null,
  passwordLifecycleLoaded: false,
  isLoaded: false,
  menuMap: {},
  permissionsMap: {},
  supplierAuthenticatedPermissions: undefined,
  mockMenuEnabled: loadMockPreference('mock_menu_enabled'),
  mockPermissionsEnabled: loadMockPreference('mock_permissions_enabled'),
  setLoginData: (token, user, expiresIn) => {
    clearSessionExpiryTimer()
    storage.token.set(token, { ttl: Math.max(0, expiresIn) * 1000 })
    storage.user.set(user)
    set({ token, user, isLoaded: true })
  },
  setSupplierSession: (token, tenantId, user, expiresAt, passwordLifecycle, permissions) => {
    const expiresAtMs = Date.parse(expiresAt)
    const ttl = expiresAtMs - Date.now()
    if (!Number.isFinite(expiresAtMs) || ttl <= 0) return false
    const normalizedUser = {
      ...user,
      id: String(user.id),
      userAccount: user.userAccount ?? user.userName,
    }
    storage.token.set(token, { ttl })
    storage.tenantId.set(tenantId)
    storage.user.set(normalizedUser)
    const authenticatedPermissions = { roles: [], permissions }
    set({
      token,
      user: normalizedUser,
      permissionsMap: {
        ...get().permissionsMap,
        'supplier-platform': authenticatedPermissions,
      },
      supplierAuthenticatedPermissions: authenticatedPermissions,
      isLoaded: true,
      passwordLifecycle,
      passwordLifecycleLoaded: true,
    })
    scheduleSessionExpiry(token, expiresAtMs)
    broadcastSupplierSessionChange('LOGIN')
    return true
  },
  setSupplierProfile: (user, passwordLifecycle) => {
    const normalizedUser = {
      ...user,
      id: String(user.id),
      userAccount: user.userAccount ?? user.userName,
    }
    storage.user.set(normalizedUser)
    set({
      user: normalizedUser,
      passwordLifecycle,
      passwordLifecycleLoaded: true,
      isLoaded: true,
    })
  },
  setPasswordLifecycle: (passwordLifecycle) => {
    set({ passwordLifecycle, passwordLifecycleLoaded: true })
  },
  setSupplierPermissions: (permissions) => {
    const authenticatedPermissions = { roles: [], permissions }
    set((state) => ({
      permissionsMap: {
        ...state.permissionsMap,
        'supplier-platform': authenticatedPermissions,
      },
      supplierAuthenticatedPermissions: authenticatedPermissions,
    }))
  },
  setCurrentUser: (user) => {
    storage.user.set(user)
    set({ user, isLoaded: true })
  },
  setSystemData: (systemCode, permissions, menuList) => {
    set((s) => ({
      permissionsMap: { ...s.permissionsMap, [systemCode]: permissions },
      menuMap: { ...s.menuMap, [systemCode]: menuList },
    }))
  },
  setMockMenuEnabled: (enabled) => {
    if (!env.dev) return
    localStorage.setItem('mock_menu_enabled', String(enabled))
    set({ mockMenuEnabled: enabled })
  },
  setMockPermissionsEnabled: (enabled) => {
    if (!env.dev) return
    localStorage.setItem('mock_permissions_enabled', String(enabled))
    set({ mockPermissionsEnabled: enabled })
  },
  setLocation: (location) => {
    storage.location.set(location)
    const currentUser = get().user
    if (currentUser) storage.user.set({ ...currentUser, location })
    set((state) => ({
      user: state.user ? { ...state.user, location } : state.user,
    }))
  },
  logout: () => {
    clearSessionExpiryTimer()
    storage.token.remove()
    storage.tenantId.remove()
    storage.user.remove()
    set({
      token: null,
      user: null,
      passwordLifecycle: null,
      passwordLifecycleLoaded: false,
      menuMap: {},
      permissionsMap: {},
      supplierAuthenticatedPermissions: undefined,
      isLoaded: false,
    })
    broadcastSupplierSessionChange('LOGOUT')
  },
  logoutIfToken: (expectedToken) => {
    if (storage.token.get() !== expectedToken) return false
    clearSessionExpiryTimer()
    storage.token.remove()
    storage.tenantId.remove()
    storage.user.remove()
    set({
      token: null,
      user: null,
      passwordLifecycle: null,
      passwordLifecycleLoaded: false,
      menuMap: {},
      permissionsMap: {},
      supplierAuthenticatedPermissions: undefined,
      isLoaded: false,
    })
    broadcastSupplierSessionChange('LOGOUT')
    return true
  },
  expireSessionIfToken: (expectedToken) => {
    if (get().token !== expectedToken) return false
    clearSessionExpiryTimer()
    storage.token.remove()
    storage.tenantId.remove()
    storage.user.remove()
    set({
      token: null,
      user: null,
      passwordLifecycle: null,
      passwordLifecycleLoaded: false,
      menuMap: {},
      permissionsMap: {},
      supplierAuthenticatedPermissions: undefined,
      isLoaded: false,
    })
    broadcastSupplierSessionChange('LOGOUT')
    return true
  },
  hasPermission: (permission, systemCode) =>
    get().permissionsMap[systemCode]?.permissions.includes(permission) ?? false,
  hasMenu: (name, systemCode) =>
    includesMenu(get().menuMap[systemCode] ?? [], name),
  hasRole: (role, systemCode) =>
    get().permissionsMap[systemCode]?.roles.includes(role) ?? false,
}))

if (initialToken && initialTokenExpiresAt !== null) {
  scheduleSessionExpiry(initialToken, initialTokenExpiresAt)
}
